import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEvents } from '../../hooks/useEvents';
import { useAttendeeData } from '../../hooks/useAttendeeData';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import { formatDate, formatDateLong, getDaysToGo, toGoogleCalendar, downloadICSFile } from '../../utils/attendeeUtils';

const AttendeeSchedulePage = () => {
  const { events: allEvents, loading: eventsLoading } = useEvents();
  const { registrations, savedEvents, loading: attendeeLoading } = useAttendeeData();
  const [search, setSearch] = useState('');
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [mobileView, setMobileView] = useState('list');

  const loading = eventsLoading || attendeeLoading;

  const registeredEventIds = useMemo(() => {
    return new Set(registrations.filter(r => r.status !== 'cancelled').map(r => r.events?.id).filter(Boolean));
  }, [registrations]);

  const savedEventIds = useMemo(() => {
    return new Set(savedEvents.map(e => e.id));
  }, [savedEvents]);

  const filteredEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return allEvents.filter(event => {
      const eventDate = new Date(event.end || event.start || event.date);
      const isPast = eventDate < today;

      if (timeFilter === 'upcoming' && isPast) return false;
      if (timeFilter === 'past' && !isPast) return false;

      if (scopeFilter === 'registered' && !registeredEventIds.has(event.id)) return false;
      if (scopeFilter === 'saved' && !savedEventIds.has(event.id)) return false;

      if (search) {
        const q = search.toLowerCase();
        const text = `${event.title || event.event || ''} ${event.city || ''} ${event.venue || ''} ${event.org || ''}`.toLowerCase();
        if (!text.includes(q)) return false;
      }

      return true;
    }).sort((a, b) => {
      const dateA = new Date(a.start || a.date);
      const dateB = new Date(b.start || b.date);
      return timeFilter === 'past' ? dateB - dateA : dateA - dateB;
    });
  }, [allEvents, timeFilter, scopeFilter, search, registeredEventIds, savedEventIds]);

  const selectedEvent = filteredEvents.find(e => e.id === selectedEventId) || filteredEvents[0];

  const handleSelectEvent = (id) => {
    setSelectedEventId(id);
    setMobileView('detail');
  };

  const handleDownloadICS = () => {
    const eventsToExport = scopeFilter === 'registered'
      ? registrations.filter(r => r.status !== 'cancelled').map(r => r.events).filter(Boolean)
      : filteredEvents;
    downloadICSFile(eventsToExport);
  };

  const getScopeBadge = (event) => {
    const isRegistered = registeredEventIds.has(event.id);
    const isSaved = savedEventIds.has(event.id);
    if (isRegistered) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
          Registered
        </span>
      );
    }
    if (isSaved) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
          Saved
        </span>
      );
    }
    return null;
  };

  const getOrgStyles = (org) => {
    switch (org?.toUpperCase()) {
      case 'UPCI': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'WPF': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case 'ALJC': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      case 'AFF': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'AIM': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 'PAW': return 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatEventDate = (event) => {
    const start = new Date(event.start || event.date);
    const end = event.end ? new Date(event.end) : null;
    if (end && start.toDateString() !== end.toDateString()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return start.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Schedule</h1>
        <LoadingState variant="list" count={5} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Schedule</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Browse all conference schedules. Events you're registered for are highlighted.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-3">
        {/* Scope + Time filters */}
        <div className="flex flex-wrap gap-2">
          {['all', 'registered', 'saved'].map(s => (
            <button
              key={s}
              onClick={() => setScopeFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                scopeFilter === s
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {s === 'all' ? 'All Events' : s === 'registered' ? 'My Registered' : 'My Saved'}
            </button>
          ))}
          <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1" />
          {['upcoming', 'past'].map(t => (
            <button
              key={t}
              onClick={() => setTimeFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                timeFilter === t
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Search + Calendar export */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by event name, city, or venue..."
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button
            onClick={handleDownloadICS}
            disabled={filteredEvents.length === 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Download Calendar
          </button>
        </div>

        {/* Active filter chips */}
        {(scopeFilter !== 'all' || search) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400">Filters:</span>
            {scopeFilter !== 'all' && (
              <span className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary rounded text-xs capitalize">
                {scopeFilter === 'registered' ? 'My Registered' : 'My Saved'}
                <button onClick={() => setScopeFilter('all')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
            {search && (
              <span className="inline-flex items-center px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                "{search}"
                <button onClick={() => setSearch('')} className="ml-1 hover:text-primary-dark">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
      </p>

      {/* Empty state */}
      {filteredEvents.length === 0 ? (
        <EmptyState
          icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          title={scopeFilter === 'registered' ? 'No registered events' : scopeFilter === 'saved' ? 'No saved events' : 'No events found'}
          message={scopeFilter === 'registered' ? 'You haven\'t registered for any events yet.' : scopeFilter === 'saved' ? 'You haven\'t saved any events yet.' : 'Try adjusting your filters or search.'}
          ctaLabel={scopeFilter !== 'all' ? 'View All Events' : null}
          ctaLink="/events"
        />
      ) : (
        <>
          {/* Mobile back button */}
          {mobileView === 'detail' && (
            <button
              onClick={() => setMobileView('list')}
              className="lg:hidden mb-4 inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to list
            </button>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Event List */}
            <div className={`lg:col-span-1 ${mobileView === 'detail' ? 'hidden lg:block' : ''}`}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {filteredEvents.map((event) => {
                    const isSelected = (selectedEvent?.id === event.id);
                    const badge = getScopeBadge(event);
                    return (
                      <button
                        key={event.id}
                        onClick={() => handleSelectEvent(event.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                          isSelected ? 'bg-primary/10 dark:bg-primary/20 border-l-4 border-primary' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {event.title || event.event}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {formatEventDate(event)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                              {event.venue ? `${event.venue}, ` : ''}{event.city}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {badge}
                            {timeFilter === 'upcoming' && (
                              <span className="text-xs text-primary font-medium">{getDaysToGo(event.start || event.date)}</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Event Detail */}
            <div className={`lg:col-span-2 ${mobileView === 'list' ? 'hidden lg:block' : ''}`}>
              {selectedEvent ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                  {/* Banner */}
                  <div className="relative h-40 bg-gradient-to-r from-primary-dark to-primary">
                    {selectedEvent.image_url && (
                      <img src={selectedEvent.image_url} alt={selectedEvent.title} className="absolute inset-0 w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="relative h-full flex flex-col justify-end p-5 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        {getScopeBadge(selectedEvent)}
                        {selectedEvent.org && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getOrgStyles(selectedEvent.org)}`}>
                            {selectedEvent.org}
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-bold">{selectedEvent.title || selectedEvent.event}</h2>
                      <p className="text-sm opacity-90 mt-1">{selectedEvent.venue}, {selectedEvent.city}</p>
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Quick info grid */}
                    <div className="grid grid-cols-2 gap-4 mb-5">
                      <div>
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Date</p>
                        <p className="text-sm text-gray-900 dark:text-white">{formatDateLong(selectedEvent.start || selectedEvent.date)}</p>
                        {selectedEvent.end && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            through {formatDate(selectedEvent.end)}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Venue</p>
                        <p className="text-sm text-gray-900 dark:text-white">{selectedEvent.venue || 'TBD'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{selectedEvent.city}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Status</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedEvent.status === 'confirmed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                            : selectedEvent.status === 'completed'
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}>
                          {selectedEvent.status === 'confirmed' ? 'Confirmed' : selectedEvent.status === 'completed' ? 'Completed' : 'Tentative'}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">Time until</p>
                        <p className="text-sm text-primary font-medium">{getDaysToGo(selectedEvent.start || selectedEvent.date)}</p>
                      </div>
                    </div>

                    {/* Description */}
                    {selectedEvent.description && (
                      <div className="mb-5">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">About</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-4">
                          {selectedEvent.description}
                        </p>
                      </div>
                    )}

                    {/* Schedule/Notes */}
                    {selectedEvent.notes && (
                      <div className="mb-5">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Schedule & Notes</h4>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed line-clamp-8">
                            {selectedEvent.notes}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {registeredEventIds.has(selectedEvent.id) ? (
                        <Link
                          to={`/my-events/${selectedEvent.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                          </svg>
                          View My Event Hub
                        </Link>
                      ) : (
                        <Link
                          to={`/events/${selectedEvent.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          View Details & Register
                        </Link>
                      )}
                      <a
                        href={toGoogleCalendar(selectedEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Add to Calendar
                      </a>
                      <Link
                        to={`/events/${selectedEvent.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        Full Details
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Select an event to view details</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AttendeeSchedulePage;
